/**
 * Check In with Dr. C — shared mobile shell.
 *
 * Design tokens: Black / White / Royal purple / Silver + faint green.
 * NO gold. Warm, calm, feminine-premium, uncluttered, one-thumb friendly.
 * Safe-area insets are respected so the layout survives modern iPhone notches.
 */
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export const CHECKIN_PALETTE = {
  ink:        "#0B0715",       // near-black surface
  paper:      "#FAFAFB",       // warm off-white
  purple:     "#5C1E8A",       // royal purple (accent)
  purpleSoft: "#8B5CF6",       // purple ambient wash
  silver:     "#C7C4CF",       // silver text/border
  silverSoft: "#E7E5EC",       // silver background pill
  green:      "#3F8F6A",       // subtle green accent (very small hint)
  danger:     "#B4223C",
};

export function CheckInShell({ title, children, footer, dark = false, testid }) {
  useDocumentMeta({
    title: title ? `${title} · Check In with Dr. C` : "Check In with Dr. C",
    description: "A personal space to pause, process, receive honest guidance, and move forward with Dr. C.",
  });
  const bg = dark ? CHECKIN_PALETTE.ink : CHECKIN_PALETTE.paper;
  const fg = dark ? "#F5F3FA" : CHECKIN_PALETTE.ink;
  return (
    <div
      className="min-h-screen w-full antialiased"
      style={{
        background: bg,
        color: fg,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid={testid || "checkin-shell"}
    >
      <div className="mx-auto w-full max-w-md px-5 sm:px-6 pt-4 pb-6 flex flex-col min-h-screen">
        {children}
        {footer ? <div className="mt-8 text-center">{footer}</div> : null}
      </div>
    </div>
  );
}

export function CheckInLogo({ small = false }) {
  return (
    <div
      className="flex items-center gap-2"
      data-testid="checkin-logo"
    >
      <span
        className={`inline-block rounded-full ${small ? "w-6 h-6" : "w-8 h-8"}`}
        style={{
          background:
            `radial-gradient(circle at 30% 30%, ${CHECKIN_PALETTE.purpleSoft}, ${CHECKIN_PALETTE.purple} 65%, ${CHECKIN_PALETTE.ink})`,
          boxShadow: "0 4px 14px -6px rgba(92,30,138,0.55)",
        }}
      />
      <span
        className={`${small ? "text-sm" : "text-base"} font-medium tracking-tight`}
        style={{ color: CHECKIN_PALETTE.ink }}
      >
        Check In with Dr. C
      </span>
    </div>
  );
}

export function PrimaryButton({ children, onClick, disabled, testid, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="w-full rounded-full py-4 px-6 text-white font-medium tracking-tight text-[15px]
                 transition-transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        background: `linear-gradient(135deg, ${CHECKIN_PALETTE.purple} 0%, ${CHECKIN_PALETTE.purpleSoft} 100%)`,
        boxShadow: "0 12px 28px -14px rgba(92,30,138,0.65)",
        minHeight: 52,
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, disabled, testid, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="w-full rounded-full py-3.5 px-6 font-medium tracking-tight text-[14.5px]
                 border transition-colors active:scale-[0.98] disabled:opacity-50"
      style={{
        borderColor: CHECKIN_PALETTE.silver,
        color: CHECKIN_PALETTE.ink,
        background: "white",
        minHeight: 48,
      }}
    >
      {children}
    </button>
  );
}

export function TextField({ label, testid, error, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold mb-1.5"
            style={{ color: CHECKIN_PALETTE.purple }}>
        {label}
      </span>
      <input
        data-testid={testid}
        className="w-full rounded-2xl border px-4 py-3.5 text-[15px] outline-none
                   focus:ring-2 focus:ring-purple-200"
        style={{
          borderColor: error ? CHECKIN_PALETTE.danger : CHECKIN_PALETTE.silver,
          background: "white",
          color: CHECKIN_PALETTE.ink,
        }}
        {...rest}
      />
      {error ? (
        <span className="mt-1 block text-xs" style={{ color: CHECKIN_PALETTE.danger }}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

export function Section({ children, className = "" }) {
  return <div className={`mt-6 ${className}`}>{children}</div>;
}
